// CookieBanner.jsx

import React, { useState, useEffect, useCallback } from 'react';
import Cookies from 'js-cookie';
import { Link } from 'react-router-dom';

// Mirrors the Cookies Notice page exactly. Only the cookies we actually use are
// listed. No marketing/advertising cookies are set, and no analytics provider is
// currently loaded — the analytics toggle is here so consent is captured if/when
// one is added.
const cookieDetails = {
  essential: {
    title: "Essential",
    description: "Necessary for the site to work — keeping your basket and remembering your cookie choice. Always on; no consent needed.",
    required: true,
    cookies: [
      { name: "fp_cart_v1", purpose: "Remembers what's in your basket", duration: "Stored locally on your device" },
      { name: "cookie_consent", purpose: "Remembers your cookie preferences", duration: "Stored locally on your device" },
    ]
  },
  analytical: {
    title: "Analytics",
    description: "Only used if you accept. Anonymous stats on which pages and plants are popular, so we can improve the shop. We don't run analytics today — if we add it, it will only load with your consent.",
    required: false,
    cookies: [
      { name: "_ga", purpose: "Distinguishes visitors (only if analytics is enabled)", duration: "2 years" },
      { name: "_gid", purpose: "Distinguishes visitors (only if analytics is enabled)", duration: "24 hours" },
    ]
  }
};

const CookieBanner = ({ onAccept = () => {} }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isBannerVisible, setIsBannerVisible] = useState(false);
  const [hasUserChosen, setHasUserChosen] = useState(false);
  const [preferences, setPreferences] = useState({
    essential: true,
    analytical: false
  });
  const [activeTab, setActiveTab] = useState('preferences');
  const [expandedSections, setExpandedSections] = useState({
    essential: false,
    analytical: false
  });

  const deleteCookie = (name) => {
    Cookies.remove(name);
  };

  const applyPreferences = useCallback((prefs) => {
    // Essential storage (basket, consent record) is app-managed and never touched
    // here. For non-essential categories we only CLEAR cookies when consent is
    // withdrawn — we never set tracking cookies directly, since no analytics or
    // marketing provider is currently loaded.
    Object.keys(cookieDetails).forEach((categoryKey) => {
      const category = cookieDetails[categoryKey];
      if (category.required) return;
      if (!prefs[categoryKey]) {
        category.cookies.forEach((cookie) => deleteCookie(cookie.name));
      }
    });
  }, []);

  const savePreferences = useCallback((prefs) => {
    try {
      localStorage.setItem('cookieConsent', JSON.stringify(prefs));
      setPreferences(prefs);
      setIsVisible(false);
      setIsBannerVisible(false);
      setHasUserChosen(true);
      applyPreferences(prefs);
      console.log('Preferences saved:', prefs);
      onAccept(); // Call the onAccept prop when preferences are saved
    } catch (error) {
      console.error('Error saving to localStorage', error);
    }
  }, [applyPreferences, onAccept]);

  const handleAcceptAll = useCallback(() => {
    savePreferences({ essential: true, analytical: true });
  }, [savePreferences]);

  const handleRejectAll = useCallback(() => {
    savePreferences({ essential: true, analytical: false });
  }, [savePreferences]);

  const handlePreferenceChange = useCallback((key) => {
    setPreferences(prev => {
      const updatedPrefs = { ...prev, [key]: !prev[key] };
      console.log(`Preference for ${key} toggled to ${updatedPrefs[key]}`);
      return updatedPrefs;
    });
  }, []);

  const handleResetPreferences = useCallback(() => {
    savePreferences({ essential: true, analytical: false });
  }, [savePreferences]);

  const toggleSection = useCallback((key) => {
    setExpandedSections(prev => {
      const updatedSections = { ...prev, [key]: !prev[key] };
      console.log(`Section ${key} toggled to ${updatedSections[key]}`);
      return updatedSections;
    });
  }, []);

  useEffect(() => {
    const consent = localStorage.getItem('cookieConsent');
    console.log('Consent from localStorage:', consent);
    if (!consent) {
      setIsBannerVisible(true);
    } else {
      try {
        const savedPreferences = JSON.parse(consent);
        console.log('Saved Preferences:', savedPreferences);
        setPreferences(savedPreferences);
        applyPreferences(savedPreferences);
        setIsBannerVisible(false);
        setHasUserChosen(true);
        onAccept(); // Call the onAccept prop when consent is already given
      } catch (error) {
        console.error('Error parsing cookieConsent from localStorage', error);
        setIsBannerVisible(true);
      }
    }
  }, [applyPreferences, onAccept]);

  const CookieWall = () => (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
      <div className="bg-canvas-raised p-8 rounded-2xl max-w-3xl w-11/12 max-h-screen overflow-y-auto shadow-lift border border-line">
        <h2 className="mb-4 font-display text-2xl text-ink">A note about cookies</h2>
        <p className="mb-6 text-ink-soft">
          We use a few essential cookies to keep your basket and remember your choices. With your
          permission we'll also use analytics to make the shop better. Nothing non-essential runs
          until you say so.
        </p>
        <div className="flex flex-col md:flex-row justify-center items-center gap-3">
          <button onClick={handleAcceptAll} className="btn-brass w-full md:w-auto">
            Accept all
          </button>
          <button onClick={handleRejectAll} className="btn-outline w-full md:w-auto">
            Reject non-essential
          </button>
          <button onClick={() => { setIsVisible(true); setIsBannerVisible(false); }} className="btn-ghost w-full md:w-auto">
            Manage cookies
          </button>
        </div>
      </div>
    </div>
  );

  const ManagementInterface = () => (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
      <div className="bg-canvas-raised p-8 rounded-2xl max-w-3xl w-11/12 max-h-screen overflow-y-auto shadow-lift border border-line">
        <h2 className="mb-4 font-display text-2xl text-ink">Cookie Consent Management</h2>
        <p className="mb-6 text-gray-600">
          We value your privacy. Please review and manage your cookie preferences below to enhance your browsing experience while maintaining control over your data.
        </p>
        <div className="flex mb-6 border-b border-gray-200">
          <button
            onClick={() => setActiveTab('preferences')}
            className={`flex-1 py-2 px-4 text-center rounded-t-md ${activeTab === 'preferences' ? 'bg-gray-600 text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}
            aria-selected={activeTab === 'preferences'}
            role="tab"
          >
            Preferences
          </button>
          <button
            onClick={() => setActiveTab('info')}
            className={`flex-1 py-2 px-4 text-center rounded-t-md ${activeTab === 'info' ? 'bg-gray-600 text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}
            aria-selected={activeTab === 'info'}
            role="tab"
          >
            More Info
          </button>
        </div>
        {activeTab === 'preferences' && (
          <div>
            {Object.keys(cookieDetails).map((key) => (
              <div key={key} className="mb-6 border-b border-gray-200 pb-4">
                <div className="flex justify-between items-center">
                  <button
                    onClick={() => toggleSection(key)}
                    className="flex items-center text-left text-gray-800 font-semibold focus:outline-none"
                    aria-expanded={expandedSections[key]}
                  >
                    <span className="mr-2">{cookieDetails[key].title}</span>
                    {cookieDetails[key].required && <span className="text-red-500">*</span>}
                    <span className="ml-2">{expandedSections[key] ? '▲' : '▼'}</span>
                  </button>
                  <label className="flex items-center text-sm text-gray-600">
                    <input
                      type="checkbox"
                      checked={preferences[key]}
                      onChange={() => handlePreferenceChange(key)}
                      disabled={cookieDetails[key].required}
                      className="mr-2"
                    />
                    {preferences[key] ? 'Enabled' : 'Disabled'}
                  </label>
                </div>
                <p className="text-sm text-gray-600 mt-2">
                  {cookieDetails[key].description}
                </p>
                {expandedSections[key] && (
                  <div className="mt-4 pl-4">
                    <p className="text-sm font-semibold">Cookies in this category:</p>
                    <ul className="list-disc list-inside text-sm text-gray-600 mt-2">
                      {cookieDetails[key].cookies.map((cookie, index) => (
                        <li key={index}>
                          <strong>{cookie.name}</strong>: {cookie.purpose} (Duration: {cookie.duration})
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
        {activeTab === 'info' && (
          <div>
            <p className="mb-6 text-gray-600">
              Our website uses cookies to improve your browsing experience and help us understand how you interact with our services. You can learn more about the types of cookies we use and how to manage your preferences in our detailed <Link to="/cookies-notice" className="text-forest-700 underline">Cookies Notice</Link>.
            </p>
            <h3 className="text-lg font-semibold text-gray-800 mb-2">Your Rights</h3>
            <p className="mb-6 text-gray-600">
              You have the right to accept or reject cookies. Essential cookies cannot be rejected as they are necessary for the website to function properly. You can change your cookie preferences at any time by clicking the 'Cookie Settings' button at the bottom right of the page.
            </p>
            <h3 className="text-lg font-semibold text-gray-800 mb-2">Contact Us</h3>
            <p className="text-gray-600">
              Any questions about cookies or your data? Email us at <a href="mailto:hello@fancyplants.ie" className="text-forest-700 underline">hello@fancyplants.ie</a>.
            </p>
          </div>
        )}
        <div className="mt-6 flex flex-wrap justify-end gap-2">
          <button onClick={handleResetPreferences} className="btn-ghost">
            Reset
          </button>
          <button onClick={handleRejectAll} className="btn-outline">
            Reject non-essential
          </button>
          <button onClick={() => savePreferences(preferences)} className="btn-forest">
            Save preferences
          </button>
          <button onClick={handleAcceptAll} className="btn-brass">
            Accept all
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <>
      {isBannerVisible && <CookieWall />}
      {isVisible && <ManagementInterface />}
      {!isBannerVisible && !isVisible && (
        <button 
          className={`fixed bottom-4 right-4 bg-gray-700 text-white rounded-full p-3 shadow-lg hover:bg-gray-800 z-50 transition-all duration-300 ${
            hasUserChosen ? 'opacity-70 hover:opacity-100' : 'opacity-100'
          }`}
          onClick={() => setIsVisible(true)}
          aria-label="Cookie Settings"
          title="Cookie Settings"
        >
          {hasUserChosen ? '🍪' : '🍪 Cookie Settings'}
        </button>
      )}
    </>
  );
};

export default CookieBanner;
